import { SuretyAccountStatus } from "@inrev/common";
import {
	useAdminFetchAccountBondingLines,
	useAdminFetchLatestSuretyAccountUnderwriting,
} from "@portal/api/account/admin";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { Icon, Text } from "@portal/components/primitives";
import { CenteredSpinner } from "@portal/components/ui/CenteredSpinner";
import { DateTime } from "luxon";
import type { AccountIdentity } from "../../../../../shared/account/AccountHeader";
import { AdminReviewReasonList } from "../../../../../shared/admin/AdminReviewReasonList";
import { BondingLineApprovalStrip } from "./BondingLineApprovalStrip";
import { BondingLinesGrid } from "./BondingLinesGrid";
import { BondingLineStateProvider } from "./state/BondingLineStateProvider";

type BondingLinesMatrixProps = {
	account: Pick<AccountIdentity, "id" | "status">;
	hideReviewReasons?: boolean;
};

export const BondingLinesMatrix = ({
	account,
	hideReviewReasons = false,
}: BondingLinesMatrixProps) => {
	const {
		bondingLines,
		bondingLinesError,
		bondingLinesIsFetching,
		bondingLinesIsLoading,
		refetchBondingLines,
	} = useAdminFetchAccountBondingLines(account.id);
	const { latestUnderwriting, latestUnderwritingError, latestUnderwritingIsLoading } =
		useAdminFetchLatestSuretyAccountUnderwriting(account.id);
	const isReview = account.status === SuretyAccountStatus.review;
	const analysisRunAt = latestUnderwriting?.createdAt
		? DateTime.fromISO(latestUnderwriting.createdAt)
		: null;

	if (bondingLinesError) {
		return (
			<ReportedRetryableLoadError
				error={bondingLinesError}
				resource="account bonding lines"
				onRetry={refetchBondingLines}
				retrying={bondingLinesIsFetching}
				testId="bonding-lines-load-error"
			/>
		);
	}
	// UW load errors retry in the account header banner; keep the matrix quiet.
	if (latestUnderwritingError) return null;
	if (bondingLinesIsLoading || latestUnderwritingIsLoading) return <CenteredSpinner />;
	if (!latestUnderwriting) {
		return (
			<div data-testid="bonding-lines-no-underwriting" className="w-full p-4 text-center">
				<Text type="body" color="secondary">
					No underwriting analysis has been run for this account, so bonding lines aren't available.
				</Text>
			</div>
		);
	}

	return (
		<BondingLineStateProvider
			account={account}
			bondingLines={bondingLines}
			underwritingId={latestUnderwriting.id}
		>
			<div className="flex flex-col w-full space-y-6">
				{!hideReviewReasons && (
					<AdminReviewReasonList reviewReasons={latestUnderwriting.reviewReasons} />
				)}

				<div className="flex items-center justify-between gap-x-6">
					<Text type="title" color="primary">
						Bonding Lines
					</Text>
					{isReview && (
						<div className="flex items-center gap-x-2">
							<Icon type="exclamation-triangle" size="small" color="warning" />
							<Text type="body">Action Required:</Text>
							<Text type="body" color="secondary">
								Review analysis and update bonding lines
							</Text>
						</div>
					)}
				</div>

				<div className="flex flex-col w-full gap-y-6">
					<BondingLineApprovalStrip account={account} underwriting={latestUnderwriting} />
					<BondingLinesGrid account={account} bondingLines={bondingLines} />
				</div>

				<Text type="caption" color="tertiary" className="italic self-end">
					Analysis last run{" "}
					{analysisRunAt?.isValid ? analysisRunAt.toLocaleString(DateTime.DATETIME_MED) : "—"}
				</Text>
			</div>
		</BondingLineStateProvider>
	);
};
